import React from 'react';
import { FlaskConical } from 'lucide-react';
import type { GameMode, GameSession, Language, SessionProgram } from '@business-game/shared';
import { FacilitatorPicker } from './FacilitatorPicker';
import { SessionDateTimePicker } from './SessionDateTimePicker';
import { SessionGameModePicker } from './SessionGameModePicker';
import { SessionLanguagePicker } from './SessionLanguagePicker';
import {
  SESSION_LANGUAGE_OPTIONS,
  SESSION_TIMEZONE_OPTIONS,
  formatTimezoneOffset,
  timezoneShortLabel,
} from './sessionFormOptions';

export type SessionFormValues = {
  name: string;
  gameMode: GameMode;
  language: Language;
  timezone: string;
  startDate: string;
  isTest: boolean;
  notes: string;
  programId: number | '';
  programSlotId: number | '';
};

type Props = {
  mode: 'create' | 'edit';
  values: SessionFormValues;
  onChange: (patch: Partial<SessionFormValues>) => void;
  sessionCode?: string;
  session?: GameSession | null;
  programs?: SessionProgram[];
  selectedProgram?: SessionProgram;
  onApplyProgramDefaults?: (programId: number | '') => void;
  onApplyProgramSlot?: (slotId: number | '') => void;
};

const fieldClass =
  'h-[50px] w-full rounded-[13px] border-[1.5px] border-slate-200 bg-white px-[15px] text-[15px] font-semibold text-slate-900 transition-all placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10';

const labelClass = 'mb-2 block text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400';

const timezoneOptions = (current: string): string[] =>
  current && !(SESSION_TIMEZONE_OPTIONS as readonly string[]).includes(current)
    ? [current, ...SESSION_TIMEZONE_OPTIONS]
    : [...SESSION_TIMEZONE_OPTIONS];

export const SessionFormPanel: React.FC<Props> = ({
  mode,
  values,
  onChange,
  programs = [],
  selectedProgram,
  onApplyProgramDefaults,
  onApplyProgramSlot,
}) => (
  <div className="space-y-5">
    <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
      <div>
        <label className={labelClass}>Nom</label>
        <input
          type="text"
          required
          placeholder="Ex: Formation Executive Mars"
          value={values.name}
          onChange={(e) => onChange({ name: e.target.value })}
          className={fieldClass}
        />
      </div>

      <div>
        <SessionLanguagePicker
          compact
          label="Langue"
          value={values.language}
          onChange={(language) => onChange({ language })}
          className="h-[50px] rounded-[13px] border-[1.5px] px-[15px]"
        />
      </div>

      <div>
        <FacilitatorPicker
          compact
          label="Animateur"
          value={values.notes}
          onChange={(notes) => onChange({ notes })}
        />
      </div>

      <div>
        <label className={labelClass}>Fuseau horaire</label>
        <select
          value={values.timezone}
          onChange={(e) => onChange({ timezone: e.target.value })}
          className={`${fieldClass} appearance-none bg-[length:16px] bg-[right_14px_center] bg-no-repeat pr-10`}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
          }}
        >
          {timezoneOptions(values.timezone).map((timezone) => (
            <option key={timezone} value={timezone}>
              {timezoneShortLabel(timezone)}
              {formatTimezoneOffset(timezone) ? ` (${formatTimezoneOffset(timezone)})` : ''}
            </option>
          ))}
        </select>
      </div>

      <div>
        <SessionDateTimePicker
          label="Date & heure"
          value={values.startDate}
          onChange={(startDate) => onChange({ startDate })}
          compact
        />
      </div>

      <div>
        <SessionGameModePicker compact label="Mode de jeu" value={values.gameMode} onChange={(gameMode) => onChange({ gameMode })} />
      </div>
    </div>

    {mode === 'create' && onApplyProgramDefaults ? (
      <div className="grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2">
        <div>
          <label className={labelClass}>Programme</label>
          <select
            value={values.programId}
            onChange={(e) => onApplyProgramDefaults(e.target.value ? Number(e.target.value) : '')}
            className={fieldClass}
          >
            <option value="">Aucun — date libre</option>
            {programs.map((program) => (
              <option key={program.id} value={program.id}>
                {program.name} ({program.slots.length})
              </option>
            ))}
          </select>
        </div>

        {selectedProgram && selectedProgram.slots.length > 0 && onApplyProgramSlot ? (
          <div>
            <label className={labelClass}>Séance</label>
            <select
              value={values.programSlotId}
              onChange={(e) => onApplyProgramSlot(e.target.value ? Number(e.target.value) : '')}
              className={fieldClass}
            >
              <option value="">Choisir…</option>
              {selectedProgram.slots.map((slot) => {
                const langLabel =
                  SESSION_LANGUAGE_OPTIONS.find((opt) => opt.value === slot.language)?.label ?? slot.language;
                return (
                  <option key={slot.id} value={slot.id}>
                    {slot.label ? `${slot.label} — ` : ''}
                    {langLabel ? `${langLabel} — ` : ''}
                    {new Date(slot.scheduledAt).toLocaleString('fr-FR', {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: slot.timezone || undefined,
                    })}
                  </option>
                );
              })}
            </select>
          </div>
        ) : null}
      </div>
    ) : null}

    <button
      type="button"
      role="switch"
      aria-checked={values.isTest}
      onClick={() => onChange({ isTest: !values.isTest })}
      className={[
        'flex w-full items-center gap-3 rounded-[13px] border-[1.5px] px-4 py-3.5 text-left transition-all',
        values.isTest ? 'border-amber-300 bg-amber-50/80' : 'border-slate-200 bg-slate-50/60 hover:border-slate-300',
      ].join(' ')}
    >
      <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[10px] bg-amber-100 text-amber-700">
        <FlaskConical className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-slate-900">Session de test</span>
        <span className="block text-xs text-slate-500">Essais et démos — exclue des statistiques du programme</span>
      </span>
      <span
        className={[
          'relative h-6 w-11 shrink-0 rounded-full transition-colors',
          values.isTest ? 'bg-amber-500' : 'bg-slate-300',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform',
            values.isTest ? 'translate-x-[22px]' : 'translate-x-0.5',
          ].join(' ')}
        />
      </span>
    </button>
  </div>
);
